import { useState } from 'react';
import { FiSave, FiTrash2, FiCheck } from 'react-icons/fi';
import {
  apiAdminUpsertShippingRate,
  apiAdminUpdateShippingZone,
  apiAdminDeleteShippingZone,
} from '../../api/shipping';
import { confirmAdminAction } from '../../utils/adminConfirm';

const NumberInput = ({ value, onChange, prefix = '' }) => (
  <span className="flex items-center gap-1">
    {prefix && <span className="text-sm text-black">{prefix}</span>}
    <input
      type="number"
      step="0.01"
      min="0"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right"
    />
  </span>
);

const ZoneRow = ({ zone, onSaved, onDeleted, isInternational = false }) => {
  const currencySymbol = isInternational ? '$' : '₹';
  const [draft, setDraft] = useState(() => ({
    baseRate: zone.rate?.baseRate ?? 0,
    additionalItemDiscountPercent: zone.rate?.additionalItemDiscountPercent ?? (isInternational ? 50 : 0),
    freeShippingThreshold: zone.rate?.freeShippingThreshold ?? 0,
    codSurcharge: zone.rate?.codSurcharge ?? 0,
    deliveryEstimate: zone.rate?.deliveryEstimate || '',
    isActive: zone.rate?.isActive !== false,
    zoneActive: zone.isActive !== false,
  }));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const setField = (key) => (val) => setDraft((d) => ({ ...d, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await apiAdminUpsertShippingRate(zone.code, {
        baseRate: Number(draft.baseRate) || 0,
        additionalItemDiscountPercent: Number(draft.additionalItemDiscountPercent) || 0,
        freeShippingThreshold: Number(draft.freeShippingThreshold) || 0,
        codSurcharge: Number(draft.codSurcharge) || 0,
        deliveryEstimate: draft.deliveryEstimate || null,
        isActive: !!draft.isActive,
      });
      if (draft.zoneActive !== (zone.isActive !== false)) {
        await apiAdminUpdateShippingZone(zone.code, { isActive: draft.zoneActive });
      }
      setSavedAt(Date.now());
      onSaved?.();
    } catch { /* API client displays the error toast. */ } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const confirmed = await confirmAdminAction(`Delete zone ${zone.code}? This cannot be undone.`, { title: 'Delete shipping zone', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await apiAdminDeleteShippingZone(zone.code);
      onDeleted?.();
    } catch { /* API client displays the error toast. */ }
  };

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-3 py-3 align-top">
        <span className="font-mono text-sm font-bold text-black">{zone.code}</span>
        {zone.scope !== 'international' && (
          <p className="text-xs text-black mt-1">{zone.name}</p>
        )}
      </td>
      <td className="px-3 py-3 align-top min-w-48">
        <p className="text-xs font-medium text-black">{zone.countries?.length || 0} countries</p>
        <p className="text-xs text-black mt-1 max-w-64 line-clamp-3">
          {zone.countries?.map(({ name }) => name).join(', ') || 'No countries assigned'}
        </p>
      </td>
      <td className="px-3 py-3">
        <NumberInput value={draft.baseRate} onChange={setField('baseRate')} prefix={currencySymbol} />
      </td>
      {isInternational && (
        <td className="px-3 py-3">
          <NumberInput
            value={draft.additionalItemDiscountPercent}
            onChange={setField('additionalItemDiscountPercent')}
            prefix="%"
          />
          <p className="mt-1 max-w-32 text-[10px] text-gray-500">Applied from item 2 onward</p>
        </td>
      )}
      {!isInternational && (
        <td className="px-3 py-3">
          <NumberInput
            value={draft.freeShippingThreshold}
            onChange={setField('freeShippingThreshold')}
            prefix={currencySymbol}
          />
        </td>
      )}
      {!isInternational && (
        <td className="px-3 py-3">
          <NumberInput value={draft.codSurcharge} onChange={setField('codSurcharge')} prefix={currencySymbol} />
        </td>
      )}
      <td className="px-3 py-3">
        <input
          type="text"
          value={draft.deliveryEstimate}
          onChange={(e) => setField('deliveryEstimate')(e.target.value)}
          placeholder="e.g. 3-5 days"
          className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
        />
      </td>
      <td className="px-3 py-3">
        <label className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={draft.zoneActive}
            onChange={(e) => setField('zoneActive')(e.target.checked)}
          />
          Active
        </label>
      </td>
      <td className="px-3 py-3 align-top">
        <span className="flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1 bg-orange-600 text-white px-3 py-1.5 rounded text-xs hover:bg-orange-700 disabled:opacity-50"
          >
            <FiSave /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={remove}
            className="flex items-center gap-1 border border-red-300 text-red-600 px-2 py-1.5 rounded text-xs hover:bg-red-50"
            aria-label="Delete zone"
          >
            <FiTrash2 />
          </button>
        </span>
        {savedAt && (
          <p className="text-[10px] text-green-600 mt-1 flex items-center gap-1">
            <FiCheck /> Saved
          </p>
        )}
      </td>
    </tr>
  );
};

export default ZoneRow;
